import { Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { Observable } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { BusinessAccountModel } from "src/app/models/business-account-model";
import { DeleteBusinessAccountModel } from "src/app/models/delete-business-account-model";
import { BusinessAccountService } from "src/app/services/business-account.service";
import { BusinessAccountDetailState } from "./business-account-detail.state";

const initialState: BusinessAccountDetailState = {
    loading: false,
    loaded: false,
    success: false,
    updateSuccess: false,
    deleteLoading : false,
    deleteLoaded : false,
    deleteSuccess: false,
    errorMessage: null,
    businessAccount: null
};

@Injectable({
    providedIn: 'root'
})
export class BusinessAccountDetailComponentStore extends ComponentStore<BusinessAccountDetailState> {

    constructor(private readonly businessAccountService: BusinessAccountService) {
        super(initialState);
    }

    readonly businessAccount$: Observable<BusinessAccountModel> = this.select(state => state.businessAccount);
    readonly loading$: Observable<boolean> = this.select(state => state.loading);
    readonly loaded$: Observable<boolean> = this.select(state => state.loaded);
    readonly success$: Observable<boolean> = this.select(state => state.success);
    readonly updateSuccess$: Observable<boolean> = this.select(state => state.updateSuccess);
    readonly deleteLoading$: Observable<boolean> = this.select(state => state.deleteLoading);
    readonly deleteLoaded$: Observable<boolean> = this.select(state => state.deleteLoaded);
    readonly deleteSuccess$: Observable<boolean> = this.select(state => state.deleteSuccess);
    readonly errorMessage$: Observable<any> = this.select(state => state.errorMessage);

    readonly viewModel$ = this.select(
        this.businessAccount$,
        this.loaded$,
        this.loading$,
        this.success$,
        this.updateSuccess$,
        this.deleteLoading$,
        this.deleteLoaded$,
        this.deleteSuccess$,
        this.errorMessage$,
        (businessAccount, loaded, loading, success, updateSuccess, deleteLoading, deleteLoaded, deleteSuccess, errorMessage) => ({
            businessAccount, loaded, loading, success, updateSuccess, deleteLoading, deleteLoaded, deleteSuccess, errorMessage
        })
    );

    readonly setInitial = this.updater((_: BusinessAccountDetailState) => {
        return {
            ...initialState
        };
    });

    readonly setInitialAfterUpdate = this.updater((state: BusinessAccountDetailState) => {
        return {
            ...state,
            loading: false,
            loaded: false,
            success: false,
            updateSuccess: false,
            errorMessage: null,
        };
    });

    readonly setLoading = this.updater((state: BusinessAccountDetailState) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    });

    readonly setLoaded = this.updater((state: BusinessAccountDetailState) => {
        return {
            ...state,
            loading: false,
            loaded: true,
        };
    });

    readonly retrieveDetailState = this.updater((state: BusinessAccountDetailState, value: {
        businessAccount: BusinessAccountModel
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: true,
            businessAccount: value.businessAccount
        };
    });

    readonly updateDetailState = this.updater((state: BusinessAccountDetailState, value: {
        businessAccount: BusinessAccountModel
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            updateSuccess: true,
            businessAccount: value.businessAccount
        };
    });

    readonly setDeleteLoading = this.updater((state: BusinessAccountDetailState) => {
        return {
            ...state,
            deleteLoading: true,
            deleteLoaded: false,
        };
    });

    readonly setDeleteLoaded = this.updater((state: BusinessAccountDetailState) => {
        return {
            ...state,
            deleteLoading: false,
            deleteLoaded: true,
        };
    });

    readonly updateDeleteState = this.updater((state: BusinessAccountDetailState, value: {
        deleted: boolean
    }) => {
        return {
            ...state,
            deleteLoading: false,
            deleteLoaded: true,
            deleteSuccess: true,
            updateSuccess: false,
            businessAccount: null,
            errorMessage: null
        };
    });

    readonly setError = this.updater((state: BusinessAccountDetailState, value: {
        errorMessage: string
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: false,
            errorMessage: value.errorMessage,
        };
    });

    readonly deleteBusinessAccount = this.effect((detailParams$: Observable<{ deleteBusinessAccount: DeleteBusinessAccountModel }>) => {
        this.setDeleteLoading();
        return detailParams$.pipe(
            switchMap((params => this.businessAccountService.deleteBusinessAccount(params.deleteBusinessAccount).pipe(
                map(result => {
                        this.updateDeleteState({ deleted: result });
                        this.setDeleteLoaded();
                        this.setInitial();
                }),
                catchError(async (error) => {
                    this.setError({ errorMessage: error });
                    this.setDeleteLoaded();
                    this.setInitial();
                })
            )))
        );
    });
}